import { Controller } from "@hotwired/stimulus"
import TomSelect from "@lib/custom-tom-select"
import { getCompanyType } from "@lib/utils"

// Connects to data-controller="organisation-lookup"
export default class extends Controller {
  
  static targets = ["input", "details", "organisationName", "organisationType", "siren", "siret", "taxNumber", "street1", "street2", "postcode", "town"]
  static values = { pappersKey: String }
  
  connect() {
    // Destroy existing Tom Select instance if it exists (e.g., from previous connection)
    if (this.inputTarget.tomselect) {
      this.inputTarget.tomselect.destroy()
    }

    this.select = new TomSelect(this.inputTarget, {
      maxItems: 1,
      load: this.load.bind(this),
      onItemAdd: this.onItemAdd.bind(this),
      labelField: "label",
      valueField: "value",
      searchField: "label",
      shouldLoad: function(query) {
        return true;  // Always trigger the load function (so we can clear the options on blank queries)
      }
    })
  }

  disconnect() {
    if (this.select) {
      this.select.destroy()
    }
  }

  async load(query, callback) {
    this.select.clearSelect()
    if (!query.trim()) {
      callback([])
      return
    }
    const queryUrl = `https://suggestions.pappers.fr/v2?q=${query}`
    const response = await fetch(queryUrl, { headers: { accept: "application/json" } })
    const data = await response.json()
    let results = data?.resultats_nom_entreprise
    if (!results?.length) return callback([])
    results = results.map((item) => {
      const label = `${item.nom_entreprise} - ${item.siren_formate} - ${item.domaine_activite}`
      return { label, value: item.siren, appliedLabel: item.nom_entreprise, item }
    })
    callback(results)
  }

  toggleDetails(e) {
    if (e.newState == "open") {
      this.select.focus()
    } else {
      this.organisationNameTarget.focus()
    }
  }

  // Run when an item is selected
  async onItemAdd(value) {
    
    // Grab the item from the select options
    const item = this.select.options[value]?.item

    // If no item found, return
    if (!item) return
    
    // Start building out an organisation object to suit our needs
    let organisation = {
      name: item.nom_entreprise,
      siren: item.siren,
      type: getCompanyType(item.forme_juridique),
    }
    
    // A lot of further data is stored in the siege object
    const siege = item.siege
    
    // If we have the siege data we can add address data to the organisation object
    if (siege) {
      if (siege.siret) organisation.siret = siege.siret
      if (siege.adresse_ligne_1) organisation.street_1 = siege.adresse_ligne_1
      if (siege.adresse_ligne_2) organisation.street_2 = siege.adresse_ligne_2
      if (siege.code_postal) organisation.postcode = siege.code_postal
      if (siege.ville) organisation.town = siege.ville
    }

    // If we have a siret (and the Papper API key), we can try to fetch the tax number from Pappers company API
    if (organisation.siret && this.pappersKeyValue) {
      const url = `https://api.pappers.fr/v2/entreprise?api_token=${this.pappersKeyValue}&siret=${organisation.siret}`
      const response = await fetch(url, { headers: { accept: "application/json" } })
      const companyData = await response.json()
      if (companyData?.numero_tva_intracommunautaire) {
        organisation.tax_number = companyData.numero_tva_intracommunautaire
      }
    }

    // Update the HTML form values
    this.organisationNameTarget.value = organisation.name || ""
    this.organisationTypeTarget.value = organisation.type || ""
    this.sirenTarget.value = organisation.siren || ""
    this.siretTarget.value = organisation.siret || ""
    this.taxNumberTarget.value = organisation.tax_number || ""
    this.street1Target.value = organisation.street_1 || ""
    this.street2Target.value = organisation.street_2 || ""
    this.postcodeTarget.value = organisation.postcode || ""
    this.townTarget.value = organisation.town || ""

    // Clear the select input
    this.select.clear(true)
    this.select.clearOptions()

    // Focus the organisation name input
    this.organisationNameTarget.focus()

    // Close the details element
    this.detailsTarget.open = false
  }
  
}
